import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { Multi, rangoDe } from "../../managment/enrollmentRequests/filtrosMatriculas";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";

/*
	Barra de filtros de Pagos con las piezas de Matrículas y Promociones (Esteban, 2026-11-04: "los filtros y el search bar no
	están de acorde a lo hablado"): fecha en control segmentado (Hoy, Ayer, Esta Semana, Este Mes, Rango) y pastillas de
	selección múltiple. Reemplaza los embudos por columna y los chips sueltos.
*/
export type PresetFecha = "" | "hoy" | "ayer" | "semana" | "mes" | "rango";
export type CampoFecha = "createdAt" | "paymentDate";

export interface FiltrosPagos {
	campo: CampoFecha;
	preset: PresetFecha;
	from: string;
	to: string;
	concepts: string[];
	methods: number[];
	accounts: number[];
	statuses: number[];
	creators: number[];
}

export const FILTROS_INICIALES: FiltrosPagos = {
	campo: "createdAt", preset: "hoy", from: "", to: "", concepts: [], methods: [], accounts: [], statuses: [], creators: []
};

export const hayFiltros = (f: FiltrosPagos) =>
	f.preset !== "hoy" || f.campo !== "createdAt" || f.concepts.length > 0 || f.methods.length > 0 || f.accounts.length > 0
	|| f.statuses.length > 0 || f.creators.length > 0;

/** «Hoy» y «Esta Semana» se recalculan al pedir: si la pantalla quedó abierta desde ayer, siguen siendo hoy. */
export const fechasDe = (f: FiltrosPagos): { from: string; to: string } => {
	if (f.preset && f.preset !== "rango") {
		const r = rangoDe(f.preset);
		return { from: r.dateFrom, to: r.dateTo };
	}
	return { from: f.from, to: f.to };
};

/*
	Criterio para el servidor. «Registrados» viaja como mediodía de México (-06:00): el servidor toma el día en UTC de ese
	instante y a mediodía es el mismo día en las dos zonas, a cualquier hora (el bug de "Hoy devuelve cero después de las 6 pm",
	2026-09-21). «Pagados» es una columna DATE y viaja como aaaa-mm-dd.
*/
export const criterioDe = (f: FiltrosPagos, busqueda: string): Record<string, any> => {
	const c: Record<string, any> = {};
	const { from, to } = fechasDe(f);
	if (from && to) {
		c[f.campo] = f.campo === "createdAt"
			? { start: `${from}T12:00:00-06:00`, end: `${to}T12:00:00-06:00` }
			: { start: from, end: to };
	}
	if (f.concepts.length) c.concepts = f.concepts;
	if (f.methods.length) c.paymentMethod = f.methods;
	if (f.accounts.length) c.recipientAccount = f.accounts;
	if (f.statuses.length) c.paymentStatus = f.statuses;
	if (f.creators.length) c.createdBy = f.creators;
	if (busqueda.trim()) c.search = busqueda.trim();
	return c;
};

/** Mismo candado que el servidor: el Excel lleva fechas, salvo que se busque algo concreto. */
export const reportePermitido = (c: Record<string, any>) =>
	!!(c.createdAt || c.paymentDate || c.search || c.id);

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const corta = (s: string) => {
	const [y, m, d] = s.split("-").map(Number);
	return y ? `${d} ${MESES[m - 1]}` : "";
};

export interface OpcionesPagos {
	concepts: { id: string; name: string }[];
	methods: { id: number; name: string }[];
	accounts: { id: number; name: string }[];
	statuses: { id: number; name: string }[];
	creators: { id: number; name: string }[];
}

interface Props {
	valor: FiltrosPagos;
	opciones: OpcionesPagos;
	onChange: (v: FiltrosPagos) => void;
	/** El buscador va en la misma fila que la fecha, a la derecha. */
	buscador: React.ReactNode;
	/** Al final de la fila de pastillas: Descargar Reporte. */
	accion?: React.ReactNode;
	movil: boolean;
}

const ETIQUETA_CAMPO: Record<CampoFecha, string> = { createdAt: "Registrados", paymentDate: "Pagados" };

const FiltrosPagosBarra: React.FC<Props> = ({ valor, opciones, onChange, buscador, accion, movil }) => {
	const [desde, setDesde] = useState(valor.from);
	const [hasta, setHasta] = useState(valor.to);
	const [abierto, setAbierto] = useState(false);

	const elegirPreset = (p: PresetFecha) => {
		if (valor.preset === p) {
			onChange({ ...valor, preset: "", from: "", to: "" });
			return;
		}
		const r = rangoDe(p);
		onChange({ ...valor, preset: p, from: r.dateFrom, to: r.dateTo });
	};
	const aplicarRango = () => {
		const [a, b] = desde && hasta && desde > hasta ? [hasta, desde] : [desde, hasta || desde];
		onChange({ ...valor, preset: a ? "rango" : "", from: a, to: b });
		setAbierto(false);
	};
	const textoRango = valor.preset === "rango" && valor.from
		? (valor.from === valor.to ? corta(valor.from) : `${corta(valor.from)} – ${corta(valor.to)}`) : "";
	const presets: { key: PresetFecha; label: string }[] = [
		{ key: "hoy", label: "Hoy" }, { key: "ayer", label: "Ayer" }, { key: "semana", label: "Esta Semana" }, { key: "mes", label: "Este Mes" }
	];

	return (
		<div className="wpgFiltros">
			<div className="wpgFila">
				<div className="waFmBarraFecha">
					<Dropdown>
						<Dropdown.Toggle as="button" type="button" className="wpgCampo">
							{ETIQUETA_CAMPO[valor.campo]}<FontAwesomeIcon icon={solid("chevron-down")} />
						</Dropdown.Toggle>
						<Dropdown.Menu className="waFmMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
							<div className="waFmLista">
								{(Object.keys(ETIQUETA_CAMPO) as CampoFecha[]).map((k) => (
									<Dropdown.Item as="button" type="button" key={k} className={"waFmOpcion" + (valor.campo === k ? " elegida" : "")}
										onClick={() => onChange({ ...valor, campo: k })}>
										<span className="waFmNombre">{ETIQUETA_CAMPO[k]}</span>
										{valor.campo === k ? <FontAwesomeIcon icon={solid("check")} style={{ color: "var(--wa-acento)" }} /> : null}
									</Dropdown.Item>
								))}
							</div>
						</Dropdown.Menu>
					</Dropdown>
					<div className="waFmSegs">
						{presets.map((p) => (
							<button key={p.key} type="button" className={"waFmSeg" + (valor.preset === p.key ? " activo" : "")} onClick={() => elegirPreset(p.key)}>{p.label}</button>
						))}
						<Dropdown show={abierto} onToggle={(s) => { setAbierto(s); if (s) { setDesde(valor.from); setHasta(valor.to); } }} autoClose="outside">
							<Dropdown.Toggle as="button" type="button" className={"waFmSeg waFmSegRango" + (valor.preset === "rango" ? " activo" : "")}>
								<FontAwesomeIcon icon={solid("calendar-days")} />&nbsp;{textoRango || "Rango"}
							</Dropdown.Toggle>
							<Dropdown.Menu className="waFmMenu waFmRangoMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
								<div className="waFmRangoCampos">
									<label>Desde<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} /></label>
									<label>Hasta<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} /></label>
								</div>
								<div className="waFmRangoPie">
									<button type="button" className="btn btn-link btn-sm" onClick={() => { setDesde(""); setHasta(""); }}>Borrar</button>
									<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!desde} onClick={aplicarRango}>Aplicar</button>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</div>
				<div className="wpgBuscador" style={movil ? { flex: "1 1 100%" } : undefined}>{buscador}</div>
			</div>
			<div className="wpgListas">
			<div className="waFmBarraListas">
				<Multi titulo="Concepto" buscar={opciones.concepts.length > 8} opciones={opciones.concepts}
					elegidos={valor.concepts} onChange={(v) => onChange({ ...valor, concepts: v })} />
				<Multi titulo="Método" opciones={opciones.methods} elegidos={valor.methods} onChange={(v) => onChange({ ...valor, methods: v })} />
				<Multi titulo="Cuenta" buscar={opciones.accounts.length > 8} opciones={opciones.accounts}
					elegidos={valor.accounts} onChange={(v) => onChange({ ...valor, accounts: v })} />
				<Multi titulo="Estatus" opciones={opciones.statuses} elegidos={valor.statuses} onChange={(v) => onChange({ ...valor, statuses: v })} />
				<Multi titulo="Registrado Por" buscar={opciones.creators.length > 8} opciones={opciones.creators}
					elegidos={valor.creators} onChange={(v) => onChange({ ...valor, creators: v })} />
				{hayFiltros(valor) ? (
					<button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => onChange(FILTROS_INICIALES)}>Limpiar Filtros</button>
				) : null}
			</div>
			{accion}
			</div>
		</div>
	);
};

export default FiltrosPagosBarra;
